import React, { useRef, useState } from 'react';
import {
  LinkWithGAClickButton,
  SectionSubTitle,
  SelectionGroup,
  SelectionGroupOption,
  SelectionGroupValue,
  Typography,
} from 'common/components';
import { usePromoViewEvent } from 'common/hooks';
import { strictBreakpoints } from 'common/styles/breakpoints';
import { createElementKey } from 'utils';
import { analyticsUtils } from 'utils/analytics-utils';
import { WINDOWS_CDN_PATH, WINDOWS_ELEMENT_BLOCK } from '../../windows-constants';
import {
  BrandName,
  getActiveBrandName,
  REHAU_AND_DECEUNINCK_BRAND_REGION_LIST,
  WINDOWS_BRANDS_SELECTION_GROUP_OPTION_LIST,
} from '../windows-brands-section';
import { BRAND_INFO_MAP, WINDOWS_BRAND_BANNER_INFO_MAP, WINDOWS_BRAND_TITLE_MAP } from './windows-brands-constants';
import {
  windowsBrandsBanner,
  windowsBrandsBannerImage,
  windowsBrandsCard,
  windowsBrandsCardBody,
  windowsBrandsCardImage,
  windowsBrandsCardInfo,
  windowsBrandsCardProperties,
  windowsBrandsGrid,
  windowsBrandsSelectionGroup,
  windowsBrandsSelectionGroupItem,
  windowsBrandsTitle,
} from './windows-brands-styles';

type WindowsBrandsProps = {
  regionId: string;
}

export const WindowsBrands: React.FC<WindowsBrandsProps> = ({ regionId }) => {
  const bannerRefs = useRef<HTMLDivElement[]>([]);
  const [activeBrand, setActiveBrand] = useState(getActiveBrandName(regionId));

  const brandInfo = BRAND_INFO_MAP[activeBrand ?? ''];
  const isSelectionGroupVisible = REHAU_AND_DECEUNINCK_BRAND_REGION_LIST.includes(regionId);
  const brandTitle = (activeBrand && !isSelectionGroupVisible)
    ? WINDOWS_BRAND_TITLE_MAP[activeBrand]
    : 'Остекление от надёжных брендов';

  const promotions = brandInfo ?
    brandInfo.map(({ name }, index: number) => ({
      name: `${WINDOWS_ELEMENT_BLOCK} / ${activeBrand}`,
      creative: name,
      position: (index + 1).toString(),
    }))
    : [];

  usePromoViewEvent(bannerRefs, `${WINDOWS_ELEMENT_BLOCK} / ${activeBrand}`, promotions);

  const handleTabChange = (_: React.MouseEvent, value: SelectionGroupValue) => {
    setActiveBrand(value as BrandName);
    setActiveBrand(value as BrandName);

    const activeOption: SelectionGroupOption | undefined = WINDOWS_BRANDS_SELECTION_GROUP_OPTION_LIST.find(
      (option: SelectionGroupOption) => option.value === value
    );
    if (activeOption) {
      analyticsUtils.clickButtonEvent(WINDOWS_ELEMENT_BLOCK, activeOption.text, 'Бренд окон');
    }
  };

  if (!activeBrand || !brandInfo) {
    return null;
  }

  return (
    <>
      <div className={windowsBrandsTitle}>
        <SectionSubTitle color="text-invert" margin="m">
          {brandTitle}
        </SectionSubTitle>
        {(isSelectionGroupVisible && activeBrand) && (
          <SelectionGroup
            active={activeBrand}
            className={windowsBrandsSelectionGroup}
            selectionGroupItemClassName={windowsBrandsSelectionGroupItem}
            onChange={handleTabChange}
            optionList={WINDOWS_BRANDS_SELECTION_GROUP_OPTION_LIST}
            rounded
          />
        )}
      </div>
      <div className={windowsBrandsGrid}>
        {brandInfo.map((info, index) => (
          <div
            className={windowsBrandsCard}
            data-position={index}
            key={createElementKey('brand-info', index)}
            ref={(el: HTMLDivElement | null) => {
              if (el && bannerRefs) bannerRefs.current[index] = el;
            }}
          >
            <img
              alt="Изображение окна в разрезе"
              className={windowsBrandsCardImage}
              src={info.imgSrc}
            />
            <div className={windowsBrandsCardBody}>
              <div className={windowsBrandsCardInfo}>
                <Typography variant="minor_caption">{info.name}</Typography>
                <Typography variant="l" size="subtitle">{info.type}</Typography>
              </div>
              <ul className={windowsBrandsCardProperties}>
                {info.properties.map(({ Icon, text }, propertyIndex) => (
                  <li key={createElementKey('brand-property', propertyIndex)}>
                    <Icon strokeWidth={0.5} type="duotone" size="l" color="#60A3BD" />
                    <Typography variant="s" size='body_long'>{text}</Typography>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}

        <LinkWithGAClickButton
          className={windowsBrandsBanner}
          elementBlock={WINDOWS_ELEMENT_BLOCK}
          elementName="Типы остеклений"
          elementText={`Сертификат ${activeBrand}`}
          href={WINDOWS_BRAND_BANNER_INFO_MAP[activeBrand]}
          title="Сертификат по окнам"
        >
          <picture>
            <source srcSet={`${WINDOWS_CDN_PATH}/brands/${activeBrand}/banner-ld.jpg/banner-ld.jpg`} media={strictBreakpoints['--only-largeDesktop']} />
            <source srcSet={`${WINDOWS_CDN_PATH}/brands/${activeBrand}/banner-d.jpg/banner-d.jpg`} media={strictBreakpoints['--only-desktop']} />
            <source srcSet={`${WINDOWS_CDN_PATH}/brands/${activeBrand}/banner-t.jpg/banner-t.jpg`} media={strictBreakpoints['--only-tablet']} />
            <img
              alt="Баннер для скачивания сертификата"
              className={windowsBrandsBannerImage}
              src={`${WINDOWS_CDN_PATH}/brands/${activeBrand}/banner-m.jpg/banner-m.jpg`}
            />
          </picture>
        </LinkWithGAClickButton>
      </div>
    </>
  );
};
